<template>
  <div>
    <DirectUploadDropzone
      :on-file-added="handleFileAdded"
      :on-file-progress="handleFileProgress"
      :on-file-uploaded="handleFileUploaded"
      :accept="accept"
    >
      <div v-if="!doesFileExist" :class="buttonClasses">
        <i v-if="props.highlightAi" class="fa fa-wand-magic-sparkles tw-me-1" />
        {{ btnTitle }}
      </div>
    </DirectUploadDropzone>
    <FileBadge
      v-if="doesFileExist"
      :name="fileName"
      :progress="file.progress"
      :loading="file.loading"
      :file-size="fileSize"
      :on-remove="handleRemove"
    />
  </div>
</template>
<script setup lang="ts">
import type { Blob as ActiveBlob } from '@rails/activestorage'
import type { FileWithProgress } from './types'

import { computed, ref } from 'vue'
import FileBadge from './file-badge.vue'
import DirectUploadDropzone from './direct-upload-dropzone.vue'

defineOptions({
  name: 'SingleFileDirectUploader',
})

interface Props {
  btnTitle?: string
  onFileUploaded: (_file: FileWithProgress) => void
  accept?: string
  highlightAi?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  btnTitle: '',
  accept: '',
  highlightAi: false,
})

const file = ref<FileWithProgress>({
  added: false,
  blob: null,
  file: null,
  loading: false,
  progress: '',
})

const doesFileExist = computed(() => file.value.added && file.value.file)
const fileName = computed(() => file.value.file?.name || '')
const fileSize = computed(() => file.value.file?.size || 0)
const buttonClasses = computed(() =>
  props.highlightAi ? 'sf-btn sf-btn-ai' : 'sf-btn'
)

const handleFileUploaded = ({ blob }: { blob: ActiveBlob }) => {
  if (file.value.added) {
    file.value.blob = blob
    file.value.loading = false

    props.onFileUploaded(file.value)
  }
}

const handleFileAdded = ({ file: fileToAdd }: { file: File }) => {
  file.value.file = fileToAdd
  file.value.loading = true
  file.value.added = true
}

const handleFileProgress = ({ progress }: { progress: string }) => {
  if (file.value.added) {
    file.value.progress = progress
    file.value.loading = true
  }
}

const handleRemove = () => {
  file.value.added = false
  file.value.blob = null
  file.value.file = null
  file.value.loading = false
  file.value.progress = ''
}
</script>
